import { useEffect } from 'react'

/**
 * Locks body scroll while any caller has an active lock.
 *
 * Counter-based implementation so stacked modals work correctly:
 *   - Modal A opens → count: 1 → body locked
 *   - Modal B opens → count: 2 → body still locked
 *   - Modal A closes → count: 1 → body still locked (B is still open)
 *   - Modal B closes → count: 0 → original overflow restored
 *
 * Without the counter, the LIFO restore of `body.style.overflow` would
 * unlock the body while a sibling modal is still open.
 *
 * @param isLocked - When true, contributes one lock to the global counter.
 */
let lockCount = 0
let originalOverflow: string | null = null

export function useBodyScrollLock(isLocked: boolean) {
  useEffect(() => {
    if (!isLocked) return

    if (lockCount === 0) {
      originalOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
    }
    lockCount += 1

    return () => {
      lockCount = Math.max(0, lockCount - 1)
      if (lockCount === 0) {
        document.body.style.overflow = originalOverflow ?? ''
        originalOverflow = null
      }
    }
  }, [isLocked])
}
